import React, { useEffect, useState } from "react";
import {
  getFovuritePosts,
  getFavoriteAlerts,
  removeFavoritePost,
  removeFavoriteAlert,
} from "../../api";
import { formatToLocalDate } from "../../utils";
import {
  FavCount,
  FavEmpty,
  FavIntro,
  FavItem,
  FavLink,
  FavList,
  FavLoading,
  FavMeta,
  FavPanelHead,
  FavPanelTitleRow,
  FavStack,
  FavStarBtn,
  FavTitle,
  ProfileCard,
  ProfileCardDesc,
  ProfileCardIcon,
  ProfileCardTitle,
  ProfileCtaLink,
} from "./Profile.styles";

const stripHtml = (html) => {
  if (!html) return "";
  const doc = new DOMParser().parseFromString(html, "text/html");
  return doc.body.textContent || "";
};

const truncate = (text, max = 120) => {
  if (!text || text.length <= max) return text;
  return `${text.slice(0, max).trim()}…`;
};

const StarIcon = () => (
  <svg
    viewBox="0 0 24 24"
    fill="currentColor"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden="true"
  >
    <polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2" />
  </svg>
);

const ChevronIcon = () => (
  <svg
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden="true"
  >
    <path d="m9 18 6-6-6-6" />
  </svg>
);

/** Lucide FileText — matches Lovable Favorites posts panel */
const FileTextIcon = () => (
  <svg
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden="true"
  >
    <path d="M14.5 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V7.5L14.5 2z" />
    <polyline points="14 2 14 8 20 8" />
    <line x1="16" x2="8" y1="13" y2="13" />
    <line x1="16" x2="8" y1="17" y2="17" />
    <line x1="10" x2="8" y1="9" y2="9" />
  </svg>
);

const BellIcon = () => (
  <svg
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden="true"
  >
    <path d="M6 8a6 6 0 0 1 12 0c0 7 3 9 3 9H3s3-2 3-9" />
    <path d="M10.3 21a1.94 1.94 0 0 0 3.4 0" />
  </svg>
);

const LoaderIcon = () => (
  <svg
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden="true"
  >
    <path d="M21 12a9 9 0 1 1-6.219-8.56" />
  </svg>
);

const FavoritesPanel = () => {
  const [posts, setPosts] = useState([]);
  const [alerts, setAlerts] = useState([]);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const [postsRes, alertsRes] = await Promise.all([
          getFovuritePosts(),
          getFavoriteAlerts(),
        ]);
        if (cancelled) return;
        setPosts(postsRes.data || []);
        setAlerts(
          (alertsRes.data || []).filter((a) => a.content !== undefined)
        );
      } catch {
        /* keep empty */
      } finally {
        if (!cancelled) setIsLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  const removePost = async (id) => {
    await removeFavoritePost(id);
    setPosts((prev) => prev.filter((p) => p.id !== id));
  };

  const removeAlert = async (id) => {
    await removeFavoriteAlert(id);
    setAlerts((prev) => prev.filter((a) => a.id !== id));
  };

  if (isLoading) {
    return (
      <FavLoading>
        <LoaderIcon />
        Loading your favorites…
      </FavLoading>
    );
  }

  const renderPanel = ({
    title,
    icon,
    items,
    emptyText,
    emptyCta,
    emptyTo,
    renderItem,
  }) => (
    <ProfileCard>
      <FavPanelHead>
        <FavPanelTitleRow>
          <ProfileCardIcon>{icon}</ProfileCardIcon>
          <ProfileCardTitle as="h2">{title}</ProfileCardTitle>
        </FavPanelTitleRow>
        <FavCount>{items.length}</FavCount>
      </FavPanelHead>

      {items.length === 0 ? (
        <FavEmpty>
          <p>{emptyText}</p>
          <div style={{ marginTop: 16 }}>
            <ProfileCtaLink to={emptyTo}>{emptyCta}</ProfileCtaLink>
          </div>
        </FavEmpty>
      ) : (
        <FavList>{items.map((item) => renderItem(item))}</FavList>
      )}
    </ProfileCard>
  );

  return (
    <div>
      <FavIntro>
        <ProfileCardTitle as="h2">Your favorites</ProfileCardTitle>
        <ProfileCardDesc>
          Posts and alerts you&apos;ve starred. Remove a star to unfavorite an
          item.
        </ProfileCardDesc>
      </FavIntro>

      <FavStack>
        {renderPanel({
          title: "Posts",
          icon: <FileTextIcon />,
          items: posts,
          emptyText: "No favorite posts yet",
          emptyCta: "Browse member content",
          emptyTo: "/blogs/premium/",
          renderItem: (post) => (
            <FavItem key={post.id}>
              <FavStarBtn
                type="button"
                aria-label="Remove from favorites"
                onClick={() => removePost(post.id)}
              >
                <StarIcon />
              </FavStarBtn>
              <FavLink to={post.url}>
                <FavTitle>{post.title}</FavTitle>
                <FavMeta>
                  {formatToLocalDate(post.publishedAt)}
                  <ChevronIcon />
                </FavMeta>
              </FavLink>
            </FavItem>
          ),
        })}

        {renderPanel({
          title: "Alerts",
          icon: <BellIcon />,
          items: alerts,
          emptyText: "No favorite alerts yet",
          emptyCta: "Go to alerts",
          emptyTo: "/alerts",
          renderItem: (alert) => {
            const excerpt = truncate(stripHtml(alert.content));
            return (
              <FavItem key={alert.id}>
                <FavStarBtn
                  type="button"
                  aria-label="Remove from favorites"
                  onClick={() => removeAlert(alert.id)}
                >
                  <StarIcon />
                </FavStarBtn>
                <FavLink to={`/alerts/${alert.id}`}>
                  <FavTitle>{excerpt || "Alert"}</FavTitle>
                  <FavMeta>
                    {formatToLocalDate(alert.createdAt)}
                    <ChevronIcon />
                  </FavMeta>
                </FavLink>
              </FavItem>
            );
          },
        })}
      </FavStack>
    </div>
  );
};

export default FavoritesPanel;
